/// <amd-module name="Artifacts/Connect/tools/Conda" />
import { Shell } from "Artifacts/Connect/Constants";
import * as Res from "Artifacts/Connect/Resources";
import { CopyableCode, DetailsCard } from "Artifacts/Connect/components/Common";
import { PackagingService, TfsService } from "Artifacts/Connect/services/PackagingService";
import { ToolService } from "Artifacts/Connect/services/ToolService";
import { IToolConnectProps } from "Artifacts/Connect/tools/IToolConnectProps";
import { ICombinedFeedView } from "Artifacts/Connect/types/ICombinedFeedView";
import { IProtocol } from "Artifacts/Connect/types/IProtocol";
import { ITool, ToolId } from "Artifacts/Connect/types/ITool";
import { FWLink } from "Artifacts/Root/components/FWLink";
import { upstreamsSettingsRoute } from "Artifacts/Root/routing/Constants";
import { UpstreamSourceType } from "Artifacts/Root/web-apis/contracts/Feed";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IPageData } from "VSS/Platform/Page";
import { replaceRouteValues } from "VSS/Platform/Util/Url";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { TextField } from "VSSUI/TextField";
import * as React from "react";
import { Link as RouterLink, generatePath } from "react-router-dom";

const CondaConnectComponent: React.FC<IToolConnectProps<CondaProtocol>> = ({
  protocol,
  feed,
  packagingService,
  tfsService,
}) => {
  const [selectedTabId, setSelectedTabId] = React.useState<string>(Shell.BashLinux);
  const [envNameTemp, setEnvNameTemp] = React.useState<string>("");
  const [envName, setEnvName] = React.useState<string>("");
  const [condarcTemp, setCondarcTemp] = React.useState<string>("");
  const [condarc, setCondarc] = React.useState<string>("");
  const selectedShell = shells[selectedTabId];

  const feedUrl = protocol.getFeedUrl(packagingService, feed);
  const hasPublicCondaUpstreamSource = feed.upstreamSources.some(
    (x) =>
      x.upstreamSourceType === UpstreamSourceType.Public && x.protocol.toLowerCase() === "conda",
  );

  const envPlaceholder = <em>{envName === "" ? "{ENV}" : `${envName}`}</em>;
  const envCodePlaceholder = envName === "" ? "<([ENV])>" : envName;
  const condarcPlaceholder = <em>{condarc === "" ? "{CONDARC}" : `${condarc}`}</em>;
  const condarcCodePlaceholder = condarc === "" ? "<([CONDARC])>" : condarc;
  selectedShell.setPlaceHolder(envCodePlaceholder, condarcCodePlaceholder);

  const shellTabBar = (prefix: string) => {
    return (
      <TabBar
        onSelectedTabChanged={(x) => setSelectedTabId(x.substring(prefix.length))}
        selectedTabId={prefix + selectedTabId}
        tabSize={TabSize.Compact}
      >
        <Tab
          name={Res.Conda_BashLinuxTab}
          id={prefix + Shell.BashLinux}
          iconProps={{ iconName: "Script" }}
        />
        <Tab
          name={Res.Conda_BashMacTab}
          id={prefix + Shell.BashMac}
          iconProps={{ iconName: "Script" }}
        />
        <Tab
          name={Res.Conda_CmdExeTab}
          id={prefix + Shell.CmdExe}
          iconProps={{ iconName: "Script" }}
        />
        <Tab
          name={Res.Conda_PowerShellTab}
          id={prefix + Shell.PowerShell}
          iconProps={{ iconName: "Script" }}
        />
      </TabBar>
    );
  };

  return (
    <React.Fragment>
      {!hasPublicCondaUpstreamSource && (
        <MessageCard severity={MessageCardSeverity.Warning}>{Res.Conda_NoUpstreams}</MessageCard>
      )}

      <DetailsCard title={{ text: Res.FeedSetup, ariaLevel: 3 }}>
        <FormatComponent format={Res.Conda_FeedSetupIntro1}>
          <strong>{Res.Conda_FeedSetupIntro1_1}</strong>
        </FormatComponent>
        <div className="list-disc">
          <ul>
            <li>
              <FormatComponent format={Res.Conda_FeedSetupStep1_1}>
                <RouterLink
                  className="text-ellipsis bolt-link"
                  to={generatePath(upstreamsSettingsRoute, { feedIdentifier: feed.name })}
                >
                  {Res.Conda_FeedSetupStep1_2}
                </RouterLink>
              </FormatComponent>
            </li>
            <li>{Res.Conda_FeedSetupStep2}</li>
            <li>
              <FormatComponent format={Res.Conda_FeedSetupStep3_1}>
                <strong>{Res.Conda_FeedSetupStep3_2}</strong>
              </FormatComponent>
            </li>
          </ul>
        </div>
      </DetailsCard>

      <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
        <FormatComponent format={Res.Conda_CreateCondarc}>
          <code>.condarc</code>
        </FormatComponent>
        <CopyableCode ariaLabel={Res.ProjectSetup}>
          <pre>
            {`
# ${Res.Conda_ChannelAliasLine1}
# ${Res.Conda_ChannelAliasLine2}
channel_alias: ${feedUrl}

# ${Res.Conda_DefaultChannelsLine1}
# ${Res.Conda_DefaultChannelsLine2}
default_channels:
  - pkgs/main
  - pkgs/r
  - pkgs/msys2

# ${Res.Conda_CustomChannels}
custom_channels:
  pkgs/pro: null
`.trim()}
          </pre>
        </CopyableCode>
      </DetailsCard>

      <DetailsCard title={{ text: Res.Conda_CreateEnvironmentTitle, ariaLevel: 3 }}>
        {shellTabBar("create-env-")}
        <p>
          <FormatComponent format={Res.Conda_Substitutions}>
            <code>
              <em>{"{ENV}"}</em>
            </code>
            <code>
              <em>{"{CONDARC}"}</em>
            </code>
            <code>.condarc</code>
          </FormatComponent>
        </p>

        <div className="form-conda-group flex-center">
          <div className="manual-conda-input">
            <FormItem label={Res.Conda_PopulateCommandEnv} className="form-conda-input">
              <TextField
                value={envNameTemp}
                onChange={(_event, value) => setEnvNameTemp(value)}
              ></TextField>
            </FormItem>

            <FormItem label={Res.Conda_PopulateCommandCondarcPath} className="form-conda-input">
              <TextField
                value={condarcTemp}
                onChange={(_event, value) => setCondarcTemp(value)}
              ></TextField>
            </FormItem>
          </div>
          <Button
            text={Res.Conda_PopulateCommandButton}
            primary={true}
            onClick={() => {
              setEnvName(envNameTemp);
              setCondarc(condarcTemp);
            }}
          />
        </div>

        <h4>{Res.Step} 1</h4>
        <p>{Res.Conda_CreateEnvironment}</p>

        <CopyableCode ariaLabel={Res.Conda_CreateEnvironmentCodeLabel}>
          {formatCommandText([`conda create --offline --name ${envCodePlaceholder}`])}
        </CopyableCode>

        <h4>{Res.Step} 2</h4>
        <p>
          <FormatComponent format={Res.Conda_ImportCondarc}>
            <code>.condarc</code>
          </FormatComponent>
        </p>

        <CopyableCode ariaLabel={Res.Conda_ImportCondarcCodeLabel}>
          <pre>{formatCommandText(selectedShell.getInstallCondarcCommands())}</pre>
        </CopyableCode>
        <h4>{Res.Step} 3</h4>
        <p>
          <FormatComponent format={Res.Conda_InstallCredHelper}>
            <code>conda activate {envPlaceholder}</code>
            <code>ARTIFACTS_CONDA_TOKEN</code>
          </FormatComponent>
        </p>
        <CopyableCode ariaLabel={Res.Conda_InstallCredHelperCodeLabel}>
          {formatCommandText([
            `conda install --name ${envCodePlaceholder} --override-channels --channel https://conda.anaconda.org/microsoft/ artifacts-credprovider-conda`,
          ])}
        </CopyableCode>
        <h4>{Res.Step} 4</h4>
        <p>{Res.Conda_ActivateEnvironment}</p>
        <CopyableCode ariaLabel={Res.Conda_ActivateEnvironment}>
          {formatCommandText([`conda activate ${envCodePlaceholder}`])}
        </CopyableCode>
      </DetailsCard>

      <DetailsCard title={{ text: Res.Conda_UseInPipelinesTitle, ariaLevel: 3 }}>
        <p className="body-s">
          <FormatComponent format={Res.Conda_UseInPipelinesSeeAlso}>
            <FWLink linkId="2234365">{Res.Conda_UseInPipelinesSeeAlsoLinkText}</FWLink>
          </FormatComponent>
        </p>
        <p>
          <FormatComponent format={Res.Conda_UseInPipelines}>
            <code>
              <em>{"{ENV}"}</em>
            </code>
            <code>
              <em>{"{CONDARC}"}</em>
            </code>
            <code>.condarc</code>
          </FormatComponent>
        </p>
        {shellTabBar("pipelines-")}
        <CopyableCode ariaLabel={Res.Conda_UseInPipelinesCodeLabel}>
          <pre>{formatCommandText(buildPipelineYaml(selectedShell))}</pre>
        </CopyableCode>
      </DetailsCard>
    </React.Fragment>
  );

  function buildPipelineYaml(shell: CondaShellBase): string[] {
    const prependPathTask: ICondaYamlTask = {
      displayName: Res.Conda_UseInPipelinesCondaPathDisplayName,
      comment: Res.Conda_UseInPipelinesCondaPathComment,
      ...shell.getPipelinePrependPathTask(),
    };

    const macTask = shell.getPipelinePostPrependPathTask();

    const createEnvironmentTask: ICondaYamlTask = {
      displayName: Res.Conda_UseInPipelinesCreateEnvironmentDisplayName,
      ...shell.getPipelineCreateEnvironmentTask(),
    };

    const condaAuthenticateTask: ICondaYamlTask = {
      taskKey: "task",
      taskContent: ["CondaAuthenticate@0"],
      displayName: Res.Conda_UseInPipelinesAuthenticateCondaDisplayName,
    };

    const useEnvironmentTask: ICondaYamlTask = {
      comment: Res.Conda_UseInPipelinesUseEnvironmentComment,
      displayName: Res.Conda_UseInPipelinesUseEnvironmentDisplayName,
      ...shell.getPipelineUseEnvironmentTask(),
    };

    const tasks = [
      prependPathTask,
      macTask,
      createEnvironmentTask,
      condaAuthenticateTask,
      useEnvironmentTask,
    ];
    const activeTasks = tasks.filter((x) => !!x);
    const taskLines = activeTasks.map(buildTaskYaml);
    const yamlLines = taskLines.reduce((a, b) => [...a, "", ...b]);
    return yamlLines;
  }
};

interface ICondaYamlTask {
  taskKey: string;
  taskContent: string[];
  comment?: string;
  displayName?: string;
}

function buildTaskYaml(task: ICondaYamlTask): string[] {
  const commentLines = task.comment ? task.comment.split(/\r\n|\r|\n/).map((x) => `# ${x}`) : [];

  let taskLine;
  let taskContentLines: string[];
  if (task.taskContent.length > 1) {
    taskLine = `- ${task.taskKey}: |`;
    taskContentLines = task.taskContent.map((s) => `    ${s}`);
  } else {
    taskLine = `- ${task.taskKey}: ${task.taskContent[0]}`;
    taskContentLines = [];
  }

  const lines = [...commentLines, taskLine, ...taskContentLines];

  if (task.displayName) {
    lines.push(`  displayName: ${task.displayName}`);
  }

  return lines;
}

function formatCommandText(command: string[]): JSX.Element {
  // based on FormatComponent

  const commandText = command.join("\n");
  // This splits the format string into a sequence of [string,item#,string,item#,string]
  // For example, if format is "a <([ENV])> b <([CONDARC])> c", the split output will be ["a ", "ENV", " b ", "CONDARC", " c"]
  const formatSegments = commandText.split(/(?:<\(\[)(\w+)(?:\]\)>)/);
  const childrenOut = [];
  for (let segmentIndex = 0; segmentIndex < formatSegments.length; ++segmentIndex) {
    const segment = formatSegments[segmentIndex];
    if (segmentIndex % 2 === 0) {
      // This is a string segment
      childrenOut.push(segment);
    } else {
      // This is a replacement segment
      childrenOut.push(<em>{`{${segment}}`}</em>);
    }
  }

  return React.createElement(React.Fragment, null, ...childrenOut);
}

abstract class CondaShellBase {
  protected envCodePlaceholder = "<([ENV])>";
  protected condarcCodePlaceholder = "<([CONDARC])>";
  protected static readonly condaCreateCmd = "conda create --yes --quiet --offline --name ";
  protected static readonly condaInstallCmd = "conda install --yes --quiet numpy pandas";

  abstract getPipelinePrependPathTask(): ICondaYamlTask;
  getPipelinePostPrependPathTask(): ICondaYamlTask | null {
    return null;
  }
  abstract getPipelineCreateEnvironmentTask(): ICondaYamlTask;
  abstract getPipelineUseEnvironmentTask(): ICondaYamlTask;
  abstract getInstallCondarcCommands(): string[];
  setPlaceHolder(envCommand: string, condarcCommand: string): void {
    this.envCodePlaceholder = envCommand;
    this.condarcCodePlaceholder = condarcCommand;
  }
}

class PowerShellCondaShell extends CondaShellBase {
  private static readonly condaHookCommand = ". $env:CONDA/shell/condabin/conda-hook.ps1";
  getPipelinePrependPathTask(): ICondaYamlTask {
    return {
      taskKey: "powershell",
      taskContent: ['Write-Host "##vso[task.prependpath]$env:CONDA\\Scripts"'],
    };
  }

  getPipelineCreateEnvironmentTask(): ICondaYamlTask {
    return {
      taskKey: "powershell",
      taskContent: [
        PowerShellCondaShell.condaHookCommand,
        CondaShellBase.condaCreateCmd + this.envCodePlaceholder,
        ...this.getInstallCondarcCommands(),
      ],
    };
  }

  getPipelineUseEnvironmentTask(): ICondaYamlTask {
    return {
      taskKey: "powershell",
      taskContent: [
        PowerShellCondaShell.condaHookCommand,
        `conda activate ${this.envCodePlaceholder}`,
        "",
        `# ${Res.Conda_UseInPipelinesForExampleInstallPackages}`,
        CondaShellBase.condaInstallCmd,
      ],
    };
  }

  getInstallCondarcCommands(): string[] {
    return [
      `$prefix = (Get-CondaEnvironment | Where-Object Name -eq '${this.envCodePlaceholder}').Path`,
      `Copy-Item -LiteralPath '${this.condarcCodePlaceholder}' -Destination (Join-Path $prefix '.condarc')`,
    ];
  }
}

abstract class BashCondaShell extends CondaShellBase {
  getPipelinePrependPathTask(): ICondaYamlTask {
    return {
      taskKey: "bash",
      taskContent: ['echo "##vso[task.prependpath]$CONDA/bin"'],
    };
  }

  getPipelineCreateEnvironmentTask(): ICondaYamlTask {
    return {
      taskKey: "bash",
      taskContent: [
        CondaShellBase.condaCreateCmd + this.envCodePlaceholder,
        ...this.getInstallCondarcCommands(),
      ],
    };
  }

  getPipelineUseEnvironmentTask(): ICondaYamlTask {
    return {
      taskKey: "bash",
      taskContent: [
        `source activate ${this.envCodePlaceholder}`,
        "",
        `# ${Res.Conda_UseInPipelinesForExampleInstallPackages}`,
        CondaShellBase.condaInstallCmd,
      ],
    };
  }

  getInstallCondarcCommands(): string[] {
    return [
      `cat ${this.condarcCodePlaceholder} | conda run --no-capture-output --name ${this.envCodePlaceholder} conda config --env --stdin`,
    ];
  }
}

class LinuxBashCondaShell extends BashCondaShell {}

class MacBashCondaShell extends BashCondaShell {
  getPipelinePostPrependPathTask(): ICondaYamlTask | null {
    return {
      comment: Res.Conda_UseInPipelinesMacChownComment,
      displayName: Res.Conda_UseInPipelinesMacChownDisplayName,
      taskKey: "bash",
      taskContent: ["sudo chown -R $USER $CONDA"],
    };
  }
}

class CmdExeCondaShell extends CondaShellBase {
  getPipelinePrependPathTask(): ICondaYamlTask {
    return {
      taskKey: "script",
      taskContent: ["'echo ##vso[task.prependpath]%CONDA%\\Scripts'"],
    };
  }

  getPipelineCreateEnvironmentTask(): ICondaYamlTask {
    return {
      taskKey: "script",
      taskContent: [
        CondaShellBase.condaCreateCmd + this.envCodePlaceholder,
        ...this.getInstallCondarcCommands(),
      ],
    };
  }

  getPipelineUseEnvironmentTask(): ICondaYamlTask {
    return {
      taskKey: "script",
      taskContent: [
        `call activate ${this.envCodePlaceholder}`,
        "",
        `@REM ${Res.Conda_UseInPipelinesForExampleInstallPackages}`,
        CondaShellBase.condaInstallCmd,
      ],
    };
  }

  getInstallCondarcCommands(): string[] {
    return [
      `type ${this.condarcCodePlaceholder} | conda run --no-capture-output --name ${this.envCodePlaceholder} conda config --env --stdin`,
    ];
  }
}

const shells: { [x: string]: CondaShellBase } = {
  [Shell.PowerShell]: new PowerShellCondaShell(),
  [Shell.BashMac]: new MacBashCondaShell(),
  [Shell.BashLinux]: new LinuxBashCondaShell(),
  [Shell.CmdExe]: new CmdExeCondaShell(),
};

class CondaProtocol implements IProtocol {
  readonly name = "Conda";
  public readonly area = "Conda"; // ?
  getFeedUrl(packagingService: PackagingService, feed: ICombinedFeedView) {
    const path = replaceRouteValues("_packaging/{feedId}/{area}/{resource}", {
      feedId: feed.name,
      area: this.area,
      resource: "repo",
    });

    return packagingService
      .getUrl(feed.project, path + "/")
      .replace("://", "://token:${ARTIFACTS_CONDA_TOKEN}@");
  }
}

export class CondaTool implements ITool {
  readonly id = ToolId.Conda;
  readonly name = "Conda";
  readonly protocol = new CondaProtocol();
  readonly allowNonMembers = false;
  readonly imagePath = `ms.azure-artifacts/connect/img/conda.svg`;
  readonly Component: React.ComponentType = CondaConnectComponent;
  readonly toolLinkId = 2234298;

  public getToolLink = () => {
    return {
      href: `https://go.microsoft.com/fwlink/?linkid=${this.toolLinkId}`,
      children: `${Res.CommandLineReference}`,
    };
  };

  public isEnvironmentSupported(pageData: IPageData): boolean {
    return pageData.isHosted;
  }

  public isProtocolEnabled(pageContext: IVssPageContext): boolean {
    return (
      isFeatureFlagEnabled(pageContext, "Packaging.Feed.Conda.EnableUI", false) ||
      isFeatureFlagEnabled(pageContext, "Packaging.Feed.Conda.EnableUI.MSFT", false)
    );
  }

  supportsGet(): boolean {
    return true;
  }

  supportsPush(feed: ICombinedFeedView): boolean {
    return false;
  }

  renderComponent(
    pageContext: IVssPageContext,
    feed: ICombinedFeedView,
    packagingService: PackagingService,
    tfsService: TfsService,
  ): JSX.Element {
    return (
      <CondaConnectComponent
        pageContext={pageContext}
        protocol={this.protocol}
        feed={feed}
        packagingService={packagingService}
        tfsService={tfsService}
      />
    );
  }
}

ToolService.registerTool(new CondaTool());
